// Tolerance Chain Project (.tcp) file format.
//
// v2.2 (Phase 3 of the design reboot) reshapes the element record to
// match the in-memory canonical form (see utils/elementShape.js):
//
//   { id, name, nominal, direction, tolMode, tol, tolUpper, tolLower,
//     distribution, norm }
//
// Global `current_standard` on the envelope is gone — norms live
// per-element on `norm`. Per the schema-per-row-norm memo, this is a
// hard cut: v1.x and v2.0/2.1 files are no longer accepted on import.
// No customer files exist pre-launch, so backward-compat would only
// drag dead code forward.

import { z } from 'zod';
import { normCpk } from './cpk';

export const TCP_FORMAT = 'tolerance.app/tcp';
export const TCP_VERSION = '2.2';

const elementSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  nominal: z.number().finite(),
  direction: z.union([z.literal(1), z.literal(-1)]),
  tolMode: z.enum(['symmetric', 'asymmetric']),
  tol: z.number().finite().nonnegative(),
  tolUpper: z.number().finite(),
  tolLower: z.number().finite(),
  distribution: z.enum(['normal', 'uniform', 'triangular']),
  norm: z.string(),
  // material wurde mit Task #27 ergänzt — optional und mit Default '',
  // damit alte v2.2-Files weiter laden und neuere Files mit
  // gesetztem Material beim Re-Import nicht gestrippt werden (zod default
  // = strip unknown würde sonst das Datenfeld stillschweigend verlieren).
  material: z.string().optional().default(''),
  // C5: deaktiviertes Maß. Optional, fehlt = aktiv (alte Dateien).
  inactive: z.boolean().optional(),
  // C+17: Cpk je Maß. Optional, fehlt = 1,0 (alte Dateien).
  cpk: z.number().min(0.5).max(3).nullable().optional(),
});

const m0Schema = z.object({
  nominal: z.number().finite(),
  min: z.number().finite(),
  max: z.number().finite(),
  tolerance: z.number().finite(),
  status: z.enum(['ok', 'error', 'none', 'warning']).default('none'),
  limits: z.object({
    min: z.number().finite().nullable(),
    max: z.number().finite().nullable()
  })
});

// Per-calculation Monte-Carlo parameters. Present only for files
// saved in `montecarlo` mode (engine-internal; omitted otherwise so
// the envelope stays clean for WC / RSS chains).
const mcParamsSchema = z.object({
  samples: z.number().int().min(100).max(500_000),
  confidence: z.enum(['p99_73', 'p99_99', 'p99_9999']).default('p99_73'),
  seed: z.number().finite().nullable().optional()
});

// Wärmeausdehnungs-Block (Task #27). Optional pro Envelope —
// fehlt = disabled-Defaults beim Laden, was identisches Verhalten zum
// Pre-Task-#27-Stand ergibt. Genauso wie bei `mc` halten wir die
// Envelope-Klasse schmal: kein leerer thermal-Block bei Save, wenn
// nichts konfiguriert wurde.
const thermalSchema = z.object({
  enabled: z.boolean(),
  tMin: z.number().finite(),
  tMax: z.number().finite()
});

const dataSchema = z.object({
  mode: z.enum(['worstcase', 'rss', 'montecarlo']),
  elements: z.array(elementSchema),
  m0: m0Schema,
  mc: mcParamsSchema.optional(),
  thermal: thermalSchema.optional()
});

const tcpSchemaV2_2 = z.object({
  format: z.literal(TCP_FORMAT),
  version: z.literal('2.2'),
  metadata: z.object({
    project_name: z.string(),
    created_at: z.iso.datetime(),
    app_version: z.string().optional()
  }),
  data: dataSchema
});

/**
 * Builds the canonical v2.2 .tcp object from current store state.
 * `state` should expose: projectName, elements (canonical shape), m0,
 * mode, plus mc* fields when mode === 'montecarlo'.
 *
 * Result is structurally cloned from the live state so callers may
 * mutate it (snapshotting) without touching the store.
 */
export function serializeProject(state, { now = new Date() } = {}) {
  const data = {
    mode: state.mode,
    // inactive und cpk nur schreiben, wenn gesetzt: Maße ohne diese
    // Angaben bleiben im Dateiformat identisch zu älteren Dateien.
    elements: structuredClone(state.elements ?? []).map(({ inactive, cpk, ...rest }) => ({
      ...rest,
      ...(inactive === true ? { inactive: true } : {}),
      ...(normCpk(cpk) !== null ? { cpk } : {}),
    })),
    m0: structuredClone(state.m0)
  };
  if (state.mode === 'montecarlo') {
    data.mc = {
      samples: state.mcSamples ?? 10000,
      confidence: state.mcConfidence ?? 'p99_73',
      seed: state.mcSeed ?? null
    };
  }
  // Thermal nur in's Envelope übernehmen, wenn der User es bewusst
  // aktiviert hat — sonst bleibt der Envelope strukturell identisch
  // zum Pre-Task-#27-Stand und ältere App-Versionen, die das Feld
  // nicht kennen, sehen keinen Unterschied.
  if (state.thermal?.enabled === true) {
    data.thermal = {
      enabled: true,
      tMin: state.thermal.tMin,
      tMax: state.thermal.tMax
    };
  }
  return {
    format: TCP_FORMAT,
    version: TCP_VERSION,
    metadata: {
      project_name: state.projectName ?? 'Neues Projekt',
      created_at: now.toISOString()
    },
    data
  };
}

/**
 * Errors thrown by parseTcp carry a stable `code` so callers can
 * branch on UI messaging.
 */
export class TcpParseError extends Error {
  constructor(code, message, details) {
    super(message);
    this.name = 'TcpParseError';
    this.code = code;
    this.details = details;
  }
}

/**
 * Parses a v2.2 .tcp file into the canonical shape loadProject()
 * consumes. v1.x, v2.0, v2.1 envelopes are explicitly rejected —
 * this is a hard cut per the schema-per-row-norm memo.
 */
export function parseTcp(input) {
  let raw;
  if (typeof input === 'string') {
    try {
      raw = JSON.parse(input);
    } catch (err) {
      throw new TcpParseError('invalid_json', `Ungültiges JSON: ${err.message}`);
    }
  } else if (input && typeof input === 'object') {
    raw = input;
  } else {
    throw new TcpParseError('empty_input', 'Leere oder ungültige Eingabe');
  }

  if (!raw || raw.format !== TCP_FORMAT) {
    throw new TcpParseError(
      'unknown_format',
      'Datei ist kein .tcp-Format (Phase 3+ erwartet Version 2.2 oder neuer)'
    );
  }

  if (raw.version !== '2.2') {
    throw new TcpParseError(
      'unsupported_version',
      `Diese App-Version unterstützt nur .tcp v2.2. Datei meldet v${raw.version}.`
    );
  }

  const result = tcpSchemaV2_2.safeParse(raw);
  if (!result.success) {
    throw new TcpParseError(
      'schema_violation',
      'Datei entspricht nicht dem .tcp v2.2 Schema',
      result.error.issues
    );
  }
  const v22 = result.data;
  return {
    name: v22.metadata.project_name,
    elements: v22.data.elements,
    m0: v22.data.m0,
    mode: v22.data.mode,
    mcSamples: v22.data.mc?.samples ?? 10000,
    mcConfidence: v22.data.mc?.confidence ?? 'p99_73',
    mcSeed: v22.data.mc?.seed ?? null,
    // thermal landet im Payload, wird vom toleranceStore.loadProject
    // via cloneThermal aufgegriffen und auf disabled-Defaults
    // zurückgeworfen, wenn der Envelope keinen thermal-Block hatte.
    thermal: v22.data.thermal,
    _meta: { version: v22.version, created_at: v22.metadata.created_at }
  };
}

/**
 * Convenience: serialize + JSON.stringify. Used by the download flow.
 */
export function toTcpString(state, options) {
  return JSON.stringify(serializeProject(state, options), null, 2);
}

/**
 * Convenience: builds a safe filename from the project name.
 * Replaces whitespace with underscores and strips characters that
 * would cause issues on common filesystems.
 */
export function tcpFilename(projectName) {
  const safe = (projectName || 'toleranzprojekt')
    .replace(/\s+/g, '_')
    // eslint-disable-next-line no-control-regex -- intentional: strip control chars from filename
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, '');
  return `${safe || 'toleranzprojekt'}.tcp`;
}
